import type { NormalizedEvent } from "@/services/types/agent-gateway";
import { type ActivityPhase, activityPhaseForTool } from "./activity-groups";
import type { AgentGatewayFailureKind } from "./agent-gateway-failure";
import { deduplicateEvents, isUserAuthor } from "./event-utils";

export const MAX_MESSAGE_LENGTH = 10_000;

export type OutgoingMessageAttempt = {
  idempotencyKey: string;
  text: string;
  submittedAt: string;
  afterEventId: string | null;
  requestKind?: "message" | "workflow_start";
  status: "pending" | "failed";
  errorMessage?: string;
  failureKind?: AgentGatewayFailureKind;
  retryable?: boolean;
};

export type StreamingMessage = {
  messageKey: string;
  text: string;
};

type ToolEvent = Extract<
  NormalizedEvent,
  { type: "tool_started" | "tool_completed" }
>;

export type ActivitySummary = {
  author: string;
  createdAt: string | null;
  phase: ActivityPhase;
  active: ToolEvent[];
  completed: ToolEvent[];
  toolEvents: ToolEvent[];
};

type CreateMessageAttemptOptions = {
  idempotencyKey?: string;
  submittedAt?: string;
  afterEventId?: string | null;
  requestKind?: "message" | "workflow_start";
};

export type ConversationTimelineItem =
  | {
      kind: "event";
      event: NormalizedEvent;
    }
  | {
      kind: "activity";
      id: string;
      activity: ActivitySummary;
    }
  | {
      kind: "outgoing";
      attempt: OutgoingMessageAttempt;
    };

export function createMessageAttempt(
  text: string,
  {
    idempotencyKey = crypto.randomUUID(),
    submittedAt = new Date().toISOString(),
    afterEventId = null,
    requestKind,
  }: CreateMessageAttemptOptions = {},
): OutgoingMessageAttempt {
  return {
    idempotencyKey,
    text: text.trim(),
    submittedAt,
    afterEventId,
    ...(requestKind ? { requestKind } : {}),
    status: "pending",
  };
}

function isToolEvent(event: NormalizedEvent): event is ToolEvent {
  return event.type === "tool_started" || event.type === "tool_completed";
}

function isWaitingStatus(event: NormalizedEvent) {
  return event.type === "agent_status" && event.content.status === "waiting";
}

function progressesPastWaitingStatus(event: NormalizedEvent) {
  if (isToolEvent(event)) {
    return true;
  }

  if (event.type === "message") {
    return !isUserAuthor(event.author);
  }

  return [
    "agent_status",
    "approval_required",
    "approval_recorded",
    "artifact",
    "error",
    "input_required",
    "input_recorded",
  ].includes(event.type);
}

function removeSupersededWaitingStatuses(events: NormalizedEvent[]) {
  return events.filter(
    (event, index) =>
      !isWaitingStatus(event) ||
      !events
        .slice(index + 1)
        .some(
          (laterEvent) =>
            isWaitingStatus(laterEvent) ||
            progressesPastWaitingStatus(laterEvent),
        ),
  );
}

function collectActivityGroup(
  events: NormalizedEvent[],
  startIndex: number,
): { activity: ActivitySummary; nextIndex: number } | null {
  const firstEvent = events[startIndex];
  if (!isToolEvent(firstEvent)) {
    return null;
  }

  const phase = activityPhaseForTool(firstEvent.content.tool);
  const toolEvents: ToolEvent[] = [];
  let nextIndex = startIndex;

  while (nextIndex < events.length) {
    const event = events[nextIndex];
    if (
      !isToolEvent(event) ||
      event.author !== firstEvent.author ||
      activityPhaseForTool(event.content.tool).id !== phase.id
    ) {
      break;
    }

    toolEvents.push(event);
    nextIndex += 1;
  }

  const started: ToolEvent[] = [];
  const completed: ToolEvent[] = [];

  for (const event of toolEvents) {
    if (event.type === "tool_completed") {
      completed.push(event);
    } else {
      started.push(event);
    }
  }

  const completedByTool = new Map<string, number>();
  for (const event of completed) {
    completedByTool.set(
      event.content.tool,
      (completedByTool.get(event.content.tool) ?? 0) + 1,
    );
  }

  const active = started.filter((event) => {
    const completedCount = completedByTool.get(event.content.tool) ?? 0;
    if (completedCount === 0) {
      return true;
    }

    completedByTool.set(event.content.tool, completedCount - 1);
    return false;
  });

  return {
    activity: {
      author: firstEvent.author,
      createdAt: firstEvent.created_at,
      phase,
      active,
      completed,
      toolEvents,
    },
    nextIndex,
  };
}

export function buildConversationTimeline(
  events: NormalizedEvent[],
  outgoingAttempts: OutgoingMessageAttempt[],
): ConversationTimelineItem[] {
  const normalizedEvents = removeSupersededWaitingStatuses(
    deduplicateEvents(events),
  );
  const timeline: ConversationTimelineItem[] = [];

  for (let index = 0; index < normalizedEvents.length; ) {
    const event = normalizedEvents[index];
    const group = collectActivityGroup(normalizedEvents, index);

    if (group) {
      timeline.push({
        kind: "activity",
        id: `activity:${event.id}`,
        activity: group.activity,
      });
      index = group.nextIndex;
      continue;
    }

    timeline.push({ kind: "event", event });
    index += 1;
  }
  const attempts = [...outgoingAttempts].sort((first, second) =>
    first.submittedAt.localeCompare(second.submittedAt),
  );

  for (const attempt of attempts) {
    let insertionIndex = 0;

    if (attempt.afterEventId !== null) {
      const anchorIndex = timeline.findIndex(
        (item) =>
          item.kind === "event" && item.event.id === attempt.afterEventId,
      );

      if (anchorIndex >= 0) {
        insertionIndex = anchorIndex + 1;
      } else {
        const submittedAt = Date.parse(attempt.submittedAt);
        const firstLaterEventIndex = timeline.findIndex(
          (item) =>
            item.kind === "event" &&
            item.event.created_at !== null &&
            Date.parse(item.event.created_at) > submittedAt,
        );

        insertionIndex =
          firstLaterEventIndex >= 0 ? firstLaterEventIndex : timeline.length;
      }
    }

    while (timeline[insertionIndex]?.kind === "outgoing") {
      insertionIndex += 1;
    }

    timeline.splice(insertionIndex, 0, {
      kind: "outgoing",
      attempt,
    });
  }

  return timeline;
}

export function mergeNormalizedEvents(
  existing: NormalizedEvent[],
  incoming: NormalizedEvent[],
) {
  return deduplicateEvents([...existing, ...incoming]);
}

export function reconcileApprovalDecisionEvents(
  existing: NormalizedEvent[],
  incoming: NormalizedEvent[],
) {
  const decidedApprovalIds = new Set(
    incoming.flatMap((event) =>
      event.type === "approval_recorded" && "approval_id" in event.content
        ? [event.content.approval_id]
        : [],
    ),
  );
  const remainingEvents = existing.filter(
    (event) =>
      !(
        event.type === "approval_required" &&
        "approval_id" in event.content &&
        decidedApprovalIds.has(event.content.approval_id)
      ),
  );

  return mergeNormalizedEvents(remainingEvents, incoming);
}
